import React, { useState } from 'react';
import { Building2, Mail, MonitorSmartphone, Sparkles, ExternalLink, FileText, ChevronDown, ChevronUp, Loader2, Save, X } from 'lucide-react';
import type { VoorgesteldClientPayload } from '../utils/portalProposalThread';
import { generateVoorgesteldProposalField, performRealMatch } from '../services/geminiService';

function skillsFromCandidateRow(c: Record<string, unknown> | null): string[] {
  if (!c) return [];
  const skillSet = c.skill_set;
  if (Array.isArray(skillSet)) return skillSet.map(String).filter(Boolean).slice(0, 24);
  if (typeof skillSet === 'string') {
    return skillSet
      .split(/[,;|]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 24);
  }
  const ai = c.ai_parsed_data as Record<string, unknown> | undefined;
  if (ai?.skills && Array.isArray(ai.skills)) return (ai.skills as unknown[]).map(String).filter(Boolean).slice(0, 24);
  if (ai?.tags && Array.isArray(ai.tags)) return (ai.tags as unknown[]).map(String).filter(Boolean).slice(0, 24);
  return [];
}

function stripHtmlToPlain(s: string, maxLen: number): string {
  return s
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen);
}

export interface VoorgesteldClientProposalPanelProps {
  vacancyTitle: string;
  clientName: string;
  candidateName: string;
  matchScore?: number;
  vacancySnippet?: string;
  /** Platte CV-/profieltekst voor AI (bijv. cv_summary). */
  candidateCvSnippet?: string;
  /** `candidates.id` — voor echte CV-/AI-match uit de database. */
  aiCandidateId?: string;
  /** `vacancies.id` — voor echte vacaturetekst uit de database. */
  aiVacancyId?: string;
  /** `applications.id` — fallback om `candidate_id` op te halen als `aiCandidateId` ontbreekt. */
  aiApplicationId?: string;
  value: VoorgesteldClientPayload;
  onChange: (next: VoorgesteldClientPayload) => void;
  /** Standaard e-mail klantcontact (uit vacature). */
  defaultClientEmail?: string;
  /** M365-handtekening (HTML) voor klant-e-mail. */
  clientEmailSignature?: string | null;
  /** Open Documentbuilder om geformatteerde CV aan voorstel te koppelen. */
  onOpenDocumentBuilder?: () => void;
  /** Afzender uit linkerkolom (M365 «Verzenden als») — ter info bij klant-mail. */
  clientSendFromLabel?: string;
  clientMailBusy?: boolean;
  onClientEmailDraft?: () => void | Promise<void>;
  onClientEmailSend?: () => void | Promise<void>;
  /** User id voor server-PDF (generate-cv). */
  recruiterUserId?: string | null;
  /** Bewaar teksten en klantportaal-instellingen lokaal (zelfde apparaat), zonder te verzenden. */
  onSavePortalDraft?: () => void;
}

const defaultPayload = (): VoorgesteldClientPayload => ({
  publishToPortal: true,
  emailToClient: false,
  clientEmailTo: '',
  clientEmailCc: '',
  clientEmailBcc: '',
  clientEmailSubject: '',
  clientEmailBodyHtml: '',
  clientProposalInternalNotes: '',
  portalCvSnapshot: null,
  recruiterSummary: '',
  availableFrom: '',
  hoursPerWeek: '',
  salaryIndication: '',
  matchNotes: '',
  portalMatchScore: 0,
});

export function createDefaultVoorgesteldClientPayload(
  partial?: Partial<VoorgesteldClientPayload>
): VoorgesteldClientPayload {
  return { ...defaultPayload(), ...partial };
}

const VoorgesteldClientProposalPanel: React.FC<VoorgesteldClientProposalPanelProps> = ({
  vacancyTitle,
  clientName,
  candidateName,
  matchScore,
  vacancySnippet,
  candidateCvSnippet,
  aiCandidateId,
  aiVacancyId,
  aiApplicationId,
  value,
  onChange,
  defaultClientEmail,
  clientEmailSignature,
  onOpenDocumentBuilder,
  clientSendFromLabel,
  clientMailBusy,
  onClientEmailDraft,
  onClientEmailSend,
  onSavePortalDraft,
  recruiterUserId,
}) => {
  const patch = (p: Partial<VoorgesteldClientPayload>) => onChange({ ...value, ...p });
  const [clientSigOpen, setClientSigOpen] = useState(false);
  const [documentDetailOpen, setDocumentDetailOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [aiSummaryBusy, setAiSummaryBusy] = useState(false);
  const [aiMatchBusy, setAiMatchBusy] = useState(false);
  const [aiSummaryErr, setAiSummaryErr] = useState<string | null>(null);
  const [aiMatchErr, setAiMatchErr] = useState<string | null>(null);
  const [aiScoreBusy, setAiScoreBusy] = useState(false);
  const [aiScoreErr, setAiScoreErr] = useState<string | null>(null);

  const aiContext = {
    candidateName,
    clientName,
    vacancyTitle,
    vacancySnippet,
    candidateCvSnippet,
    matchScore,
  };

  /** AI mag draaien zolang vacaturetitel + kandidaatnaam bekend zijn; fragmenten zijn optioneel. */
  const canRunProposalAi = (): boolean =>
    (vacancyTitle || '').trim().length >= 2 && (candidateName || '').trim().length >= 1;

  const runAiField = async (field: 'recruiterSummary' | 'matchNotes') => {
    if (!canRunProposalAi()) {
      const msg = 'Vacaturetitel en kandidaatnaam zijn nodig voor AI (ontbreken in dit scherm).';
      window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: msg, type: 'error' } }));
      throw new Error(msg);
    }
    const existingText = field === 'recruiterSummary' ? value.recruiterSummary : value.matchNotes;
    return generateVoorgesteldProposalField(field, { ...aiContext, existingText });
  };

  const runAiMatchScore = async () => {
    if (!canRunProposalAi()) {
      const msg = 'Vacaturetitel en kandidaatnaam zijn nodig voor AI-match (ontbreken in dit scherm).';
      window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: msg, type: 'error' } }));
      throw new Error(msg);
    }
    const vacSnip = (vacancySnippet || '').trim();
    const cvSnip = (candidateCvSnippet || '').trim();

    const { supabase } = await import('../services/supabaseService');
    let resolvedCandidateId = String(aiCandidateId || '').trim();
    const appId = String(aiApplicationId || '').trim();
    const vacId = String(aiVacancyId || '').trim();
    if (!resolvedCandidateId && appId) {
      const { data: appRow } = await supabase.from('applications').select('candidate_id').eq('id', appId).maybeSingle();
      if (appRow?.candidate_id) resolvedCandidateId = String(appRow.candidate_id).trim();
    }

    let candRow: Record<string, unknown> | null = null;
    let vacRow: Record<string, unknown> | null = null;
    if (resolvedCandidateId) {
      const { data } = await supabase
        .from('candidates')
        .select('name, role, cv_summary, skill_set, ai_parsed_data, original_cv_text')
        .eq('id', resolvedCandidateId)
        .maybeSingle();
      if (data && typeof data === 'object') candRow = data as Record<string, unknown>;
    }
    if (vacId) {
      const { data } = await supabase.from('vacancies').select('title, description, data').eq('id', vacId).maybeSingle();
      if (data && typeof data === 'object') vacRow = data as Record<string, unknown>;
    }

    const ai = (candRow?.ai_parsed_data && typeof candRow.ai_parsed_data === 'object'
      ? candRow.ai_parsed_data
      : {}) as Record<string, unknown>;
    const summaryFromDb =
      String(candRow?.cv_summary || ai.summary || '')
        .trim()
        || String(candRow?.original_cv_text || '')
            .slice(0, 8000)
            .trim();
    const summaryForMatch =
      summaryFromDb ||
      cvSnip.slice(0, 8000) ||
      `Kandidaat: ${candidateName.trim()}. Geen CV-tekst in database of in dit scherm — geef een voorzichtige inschatting.`;

    const profile = {
      name: String(candRow?.name || candidateName || ''),
      role: String(candRow?.role || ''),
      summary: summaryForMatch,
      skills: candRow ? skillsFromCandidateRow(candRow) : skillsFromCandidateRow(null),
      timeline: Array.isArray(ai.work_experience) ? (ai.work_experience as unknown[]) : [],
    };
    if (!profile.skills.length && cvSnip) {
      profile.skills = cvSnip
        .split(/[,;|]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0 && s.length < 60)
        .slice(0, 24);
    }

    const vacData = (vacRow?.data && typeof vacRow.data === 'object' ? vacRow.data : {}) as Record<string, unknown>;
    const rawVacDesc = String(vacRow?.description || vacData.description || '');
    const fromDbVac = stripHtmlToPlain(rawVacDesc, 12000);
    const vacancyDescription = (
      fromDbVac ||
      vacSnip ||
      [
        `Vacaturetitel: ${vacancyTitle.trim()}.`,
        clientName.trim() ? `Opdrachtgever: ${clientName.trim()}.` : '',
      ]
        .filter(Boolean)
        .join(' ')
    ).trim();

    const vacancy = {
      title: String(vacRow?.title || vacancyTitle || ''),
      description: vacancyDescription || vacancyTitle,
      data: vacData,
    };

    const r = await performRealMatch(profile, vacancy, undefined);
    const score = typeof r?.score === 'number' ? Math.max(0, Math.min(100, Math.round(r.score))) : 0;
    const reason = String(r?.reason || '').trim();
    const pros: string[] = Array.isArray(r?.pros) ? r.pros.map((x: unknown) => String(x).trim()).filter(Boolean) : [];
    const cons: string[] = Array.isArray(r?.cons) ? r.cons.map((x: unknown) => String(x).trim()).filter(Boolean) : [];
    const block = [
      pros.length ? `Pluspunten:\n${pros.map((p) => `• ${p}`).join('\n')}` : '',
      cons.length ? `Aandachtspunten:\n${cons.map((p) => `• ${p}`).join('\n')}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');

    const next: Partial<VoorgesteldClientPayload> = { portalMatchScore: score };
    if (reason && !value.recruiterSummary.trim()) next.recruiterSummary = reason;
    if (block) next.matchNotes = block;
    patch(next);
  };

  const openLinkedDocumentPdf = async () => {
    const snap = value.portalCvSnapshot;
    if (!snap || typeof snap !== 'object') return;
    const o = snap as Record<string, unknown>;
    const templateId = o.templateId != null ? String(o.templateId).trim() : '';
    const rd = o.resumeData as Record<string, unknown> | undefined;
    const liveResumeData = rd && typeof rd === 'object' && !Array.isArray(rd) ? rd : null;
    const candidateFromSnap = o.candidateId != null ? String(o.candidateId).trim() : '';
    const candidateFromCv = liveResumeData && (liveResumeData as { candidateId?: string }).candidateId
      ? String((liveResumeData as { candidateId?: string }).candidateId).trim()
      : '';
    const candidateId = candidateFromSnap || candidateFromCv;
    const uid = String(recruiterUserId || '').trim();
    if (!templateId || !uid) {
      setPdfError('Geen template of gebruiker — log opnieuw in of koppel opnieuw vanuit de Documentbuilder.');
      return;
    }
    setPdfLoading(true);
    setPdfError(null);
    try {
      const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:4000' : '';
      const res = await fetch(`${apiUrl}/api/documents/generate-cv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidateId: candidateId || uid,
          templateId,
          userId: uid,
          title: o.documentName != null ? String(o.documentName) : `Voorstel — ${candidateName}`,
          pages: [],
          liveResumeData,
        }),
      });
      const docData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(docData?.error || res.statusText || 'PDF genereren mislukt');
      }
      const url = docData?.pdfPublicUrl as string | undefined;
      if (!url) throw new Error(docData?.pdfError || 'Geen PDF-URL ontvangen.');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      setPdfError(e instanceof Error ? e.message : 'PDF genereren mislukt.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-5 p-5 sm:p-6">
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Klant</p>
        <h4 className="mt-1 flex items-center gap-2 text-base font-bold text-slate-900">
          <Building2 className="h-4 w-4 text-slate-500" aria-hidden />
          {clientName || 'Opdrachtgever'}
        </h4>
        <p className="mt-1 text-sm text-slate-600">
          Vacature: <span className="font-semibold text-slate-800">{vacancyTitle}</span>
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Match klantportaal (%)</label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <input
                type="number"
                min={0}
                max={100}
                value={Number.isFinite(value.portalMatchScore) ? value.portalMatchScore : 0}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  const v = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : 0;
                  patch({ portalMatchScore: v });
                }}
                className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm font-bold text-slate-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
              <button
                type="button"
                disabled={aiScoreBusy}
                onClick={() => {
                  setAiScoreErr(null);
                  setAiScoreBusy(true);
                  void runAiMatchScore()
                    .then(() =>
                      window.dispatchEvent(
                        new CustomEvent('wt:toast', {
                          detail: { message: 'AI-match toegepast — pas het percentage gerust handmatig aan.', type: 'success' },
                        }),
                      ),
                    )
                    .catch((e: unknown) => {
                      const m = e instanceof Error ? e.message : 'AI-match mislukt.';
                      setAiScoreErr(m);
                      window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: m, type: 'error' } }));
                    })
                    .finally(() => setAiScoreBusy(false));
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 px-2.5 py-1.5 text-[9px] font-black uppercase tracking-wider text-white shadow-sm transition hover:shadow-md hover:shadow-purple-500/15 disabled:opacity-60"
              >
                {aiScoreBusy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Sparkles className="h-3 w-3" aria-hidden />}
                AI-match
              </button>
            </div>
            {aiScoreErr ? <p className="mt-1 text-xs font-medium text-red-600">{aiScoreErr}</p> : null}
            <p className="mt-1 max-w-md text-[10px] leading-snug text-slate-500">
              Dit percentage wordt opgeslagen bij de sollicitatie en getoond op de kandidaatkaart in het klantportaal (na publiceren / bevestigen).
            </p>
          </div>
          {typeof matchScore === 'number' && matchScore > 0 ? (
            <p className="text-[10px] font-semibold text-slate-400">
              Huidige pipeline-match: <span className="text-slate-700">{matchScore}%</span> (ter referentie)
            </p>
          ) : null}
        </div>
      </div>

      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white p-4 shadow-sm">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden />
          <div>
            <p className="text-sm font-bold text-indigo-950">CV matchen aan vacature</p>
            <p className="mt-1 text-xs leading-relaxed text-indigo-900/80">
              Leg in het klantportaal uit waarom deze kandidaat past. Werk het CV bij in de <strong>Documentbuilder</strong> (Word-templates
              / M365) en tik op <strong>Koppel aan voorstel</strong>. Daarna <strong>Terug naar voorstel</strong> om hier verder te gaan — dat
              ziet de klant als opgemaakt voorstel-document.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {onOpenDocumentBuilder ? (
                <button
                  type="button"
                  onClick={onOpenDocumentBuilder}
                  className="inline-flex items-center gap-2 rounded-lg border-2 border-indigo-300 bg-indigo-600 px-3 py-2 text-xs font-black uppercase tracking-wide text-white shadow-sm transition hover:bg-indigo-700"
                >
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Open Documentbuilder
                </button>
              ) : null}
              {value.portalCvSnapshot && typeof value.portalCvSnapshot === 'object' ? (
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setPdfError(null);
                      const snap = value.portalCvSnapshot as Record<string, unknown>;
                      const tid = snap?.templateId != null ? String(snap.templateId).trim() : '';
                      const uid = String(recruiterUserId || '').trim();
                      if (tid && uid) {
                        void openLinkedDocumentPdf();
                        return;
                      }
                      setDocumentDetailOpen(true);
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-900 shadow-sm transition hover:bg-emerald-100"
                  >
                    <FileText className="h-3 w-3" aria-hidden />
                    Document gekoppeld — bekijk
                  </button>
                  {(value.portalCvSnapshot as { source?: string }).source === 'documentbuilder' ? (
                    <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-900">
                      Documentbuilder
                    </span>
                  ) : (
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-600">
                      Legacy CV-builder
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-[10px] font-medium text-indigo-700/70">Nog geen opgemaakt document gekoppeld voor het portaal.</span>
              )}
            </div>
            {pdfError && !documentDetailOpen ? (
              <p className="mt-2 text-xs font-semibold text-red-600">{pdfError}</p>
            ) : null}
          </div>
        </div>
        {vacancySnippet ? (
          <div className="mt-3 max-h-28 overflow-y-auto rounded-xl border border-indigo-100 bg-white/90 p-3 text-xs leading-relaxed text-slate-600">
            {vacancySnippet}
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-500">Geen vacaturetekst geladen in dit scherm — vul hieronder je match-toelichting in.</p>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <label className="flex flex-1 cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-indigo-200">
          <input
            type="checkbox"
            checked={value.publishToPortal}
            onChange={(e) => patch({ publishToPortal: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <span className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <MonitorSmartphone className="h-4 w-4 text-indigo-600" aria-hidden />
              Klaarzetten in klantportaal
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">Zichtbaar onder Samenwerking; contactpersoon kan reageren.</span>
          </span>
        </label>
        <label className="flex flex-1 cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-indigo-200">
          <input
            type="checkbox"
            checked={value.emailToClient}
            onChange={(e) => patch({ emailToClient: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <span className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <Mail className="h-4 w-4 text-indigo-600" aria-hidden />
              E-mail naar klantcontact
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">Outlook / M365 — apart van het bericht aan de kandidaat (linkerkolom).</span>
            {clientSendFromLabel ? (
              <p className="mt-1 text-[10px] font-semibold text-slate-500">
                Afzender (linkerkolom «Verzenden als»): <span className="text-slate-800">{clientSendFromLabel}</span>
              </p>
            ) : (
              <p className="mt-1 text-[10px] text-slate-400">Afzender: je eigen M365-mailbox (tenzij je links een gedeelde mailbox kiest).</p>
            )}
          </span>
        </label>
      </div>

      {onSavePortalDraft ? (
        <div className="flex flex-wrap items-center justify-end gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
          <p className="mr-auto max-w-[min(100%,20rem)] text-[10px] text-slate-500">
            Sla je invoer voor het klantportaal en dit scherm op dit apparaat op (inclusief vinkjes en teksten). Geen e-mail naar kandidaat of klant.
          </p>
          <button
            type="button"
            onClick={onSavePortalDraft}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-indigo-800 shadow-sm transition hover:bg-indigo-50"
          >
            <Save className="h-3.5 w-3.5" aria-hidden />
            Concept voorstel opslaan
          </button>
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          Interne notities (vacature &amp; bedrijf)
        </label>
        <p className="mt-0.5 text-[10px] text-slate-400">
          Alleen voor recruiters: verschijnt bij vacature-notities en op de kandidaat-tijdlijn voor het bedrijf.
        </p>
        <textarea
          value={value.clientProposalInternalNotes || ''}
          onChange={(e) => patch({ clientProposalInternalNotes: e.target.value })}
          rows={3}
          placeholder="Bv. afspraken met de hiring manager, tarief, concurrentie…"
          className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
        />
      </div>

      {value.emailToClient ? (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Aan (klant) — primair</label>
            <input
              type="text"
              value={value.clientEmailTo}
              onChange={(e) => patch({ clientEmailTo: e.target.value })}
              placeholder={defaultClientEmail || 'contact@bedrijf.nl'}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
            {defaultClientEmail && value.clientEmailTo !== defaultClientEmail ? (
              <button
                type="button"
                className="mt-1 text-[10px] font-bold text-indigo-600 hover:underline"
                onClick={() => patch({ clientEmailTo: defaultClientEmail })}
              >
                Vul standaard: {defaultClientEmail}
              </button>
            ) : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">CC (komma&apos;s)</label>
              <input
                type="text"
                value={value.clientEmailCc || ''}
                onChange={(e) => patch({ clientEmailCc: e.target.value })}
                placeholder="extra@bedrijf.nl"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">BCC</label>
              <input
                type="text"
                value={value.clientEmailBcc || ''}
                onChange={(e) => patch({ clientEmailBcc: e.target.value })}
                placeholder="intern@werfplaats.nl"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Onderwerp</label>
            <input
              type="text"
              value={value.clientEmailSubject}
              onChange={(e) => patch({ clientEmailSubject: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Bericht (platte tekst → HTML)</label>
            <textarea
              value={value.clientEmailBodyHtml}
              onChange={(e) => patch({ clientEmailBodyHtml: e.target.value })}
              rows={8}
              placeholder="Beste …,&#10;&#10;Hierbij ons voorstel …"
              className="mt-1 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          {clientEmailSignature ? (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setClientSigOpen(!clientSigOpen)}
                className="flex w-full items-center justify-between px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50"
              >
                <span>M365-handtekening (voorbeeld — wordt meegestuurd)</span>
                {clientSigOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {clientSigOpen ? (
                <div
                  className="max-h-28 overflow-y-auto border-t border-slate-100 px-3 py-2 text-xs text-slate-600"
                  dangerouslySetInnerHTML={{ __html: clientEmailSignature }}
                />
              ) : null}
              <p className="border-t border-slate-50 px-3 py-2 text-[10px] text-slate-500">
                Bij verzenden wordt dezelfde handtekening automatisch onderaan de klant-e-mail gezet (zoals bij het bericht aan de kandidaat), tenzij je <code className="rounded bg-slate-100 px-1">[HANDTEKENING]</code> in de tekst zet.
              </p>
            </div>
          ) : null}
          {onClientEmailDraft && onClientEmailSend ? (
            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3">
              <button
                type="button"
                disabled={!!clientMailBusy}
                onClick={() => void onClientEmailDraft()}
                className="inline-flex min-w-[140px] flex-1 items-center justify-center gap-2 rounded-lg border-2 border-slate-300 bg-white px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-700 shadow-sm transition hover:border-[#0078D4] hover:text-[#0078D4] disabled:opacity-50"
              >
                {clientMailBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Concept (M365)
              </button>
              <button
                type="button"
                disabled={!!clientMailBusy}
                onClick={() => void onClientEmailSend()}
                className="inline-flex min-w-[140px] flex-1 items-center justify-center gap-2 rounded-lg bg-[#0078D4] px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition hover:bg-[#0063B1] disabled:opacity-50"
              >
                {clientMailBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Direct verzenden (M365)
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-3">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Samenvatting voor de klant</label>
            <button
              type="button"
              disabled={aiSummaryBusy}
              onClick={() => {
                setAiSummaryErr(null);
                setAiSummaryBusy(true);
                void runAiField('recruiterSummary')
                  .then((text) => patch({ recruiterSummary: text }))
                  .catch((e: unknown) => {
                    const m = e instanceof Error ? e.message : 'AI mislukt.';
                    setAiSummaryErr(m);
                    window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: m, type: 'error' } }));
                  })
                  .finally(() => setAiSummaryBusy(false));
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-white shadow-sm transition hover:shadow-md hover:shadow-purple-500/15 disabled:opacity-60"
            >
              {aiSummaryBusy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Sparkles className="h-3 w-3" aria-hidden />}
              {value.recruiterSummary.trim() ? 'Verbeter met AI' : 'Schrijf met AI'}
            </button>
          </div>
          <textarea
            value={value.recruiterSummary}
            onChange={(e) => patch({ recruiterSummary: e.target.value })}
            rows={4}
            placeholder="Korte pitch waarom deze kandidaat interessant is…"
            className="mt-1 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
          />
          {aiSummaryErr ? <p className="mt-1 text-xs font-medium text-red-600">{aiSummaryErr}</p> : null}
        </div>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Match CV ↔ vacature</label>
            <button
              type="button"
              disabled={aiMatchBusy}
              onClick={() => {
                setAiMatchErr(null);
                setAiMatchBusy(true);
                void runAiField('matchNotes')
                  .then((text) => patch({ matchNotes: text }))
                  .catch((e: unknown) => {
                    const m = e instanceof Error ? e.message : 'AI mislukt.';
                    setAiMatchErr(m);
                    window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: m, type: 'error' } }));
                  })
                  .finally(() => setAiMatchBusy(false));
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-white shadow-sm transition hover:shadow-md hover:shadow-purple-500/15 disabled:opacity-60"
            >
              {aiMatchBusy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Sparkles className="h-3 w-3" aria-hidden />}
              {value.matchNotes.trim() ? 'Verbeter met AI' : 'Schrijf met AI'}
            </button>
          </div>
          <textarea
            value={value.matchNotes}
            onChange={(e) => patch({ matchNotes: e.target.value })}
            rows={4}
            placeholder="Concrete aansluiting op functie-eisen, ervaring, cultuur…"
            className="mt-1 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
          />
          {aiMatchErr ? <p className="mt-1 text-xs font-medium text-red-600">{aiMatchErr}</p> : null}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Beschikbaar vanaf</label>
            <input
              type="date"
              value={value.availableFrom}
              onChange={(e) => patch({ availableFrom: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Uren / week</label>
            <input
              type="text"
              value={value.hoursPerWeek}
              onChange={(e) => patch({ hoursPerWeek: e.target.value })}
              placeholder="bijv. 36"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Salarisindicatie</label>
            <input
              type="text"
              value={value.salaryIndication}
              onChange={(e) => patch({ salaryIndication: e.target.value })}
              placeholder="bijv. € 4.500 – € 5.200"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
        </div>
      </div>

      {documentDetailOpen && value.portalCvSnapshot && typeof value.portalCvSnapshot === 'object' ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setDocumentDetailOpen(false);
            setPdfError(null);
          }}
        >
          <div
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 border-b border-slate-100 pb-3">
              <h4 className="text-sm font-black uppercase tracking-wide text-slate-900">Gekoppeld document</h4>
              <button
                type="button"
                onClick={() => {
                  setDocumentDetailOpen(false);
                  setPdfError(null);
                }}
                className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-800"
                aria-label="Sluiten"
              >
                <X size={18} />
              </button>
            </div>
            {(() => {
              const snap = value.portalCvSnapshot as Record<string, unknown>;
              const docName = snap.documentName != null ? String(snap.documentName) : '—';
              const src = snap.source != null ? String(snap.source) : '—';
              const attached = snap.attachedAt != null ? String(snap.attachedAt) : '—';
              const tmpl = snap.templateId != null ? String(snap.templateId) : '—';
              const rd = snap.resumeData;
              const hasResume = rd && typeof rd === 'object' && !Array.isArray(rd);
              return (
                <dl className="mt-4 space-y-3 text-sm text-slate-800">
                  <div>
                    <dt className="text-[10px] font-black uppercase text-slate-400">Documentnaam (in builder)</dt>
                    <dd className="mt-0.5 font-semibold">{docName}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-black uppercase text-slate-400">Bron</dt>
                    <dd className="mt-0.5 font-semibold">{src === 'documentbuilder' ? 'Documentbuilder' : src}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-black uppercase text-slate-400">Template-id</dt>
                    <dd className="mt-0.5 break-all font-mono text-xs">{tmpl}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-black uppercase text-slate-400">Gekoppeld op (ISO)</dt>
                    <dd className="mt-0.5 font-semibold">{attached}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-black uppercase text-slate-400">CV-structuur</dt>
                    <dd className="mt-0.5 text-xs text-slate-600">
                      {hasResume ? 'Opgeslagen opbouw (koppen, ervaring, opleiding) zit in dit voorstel.' : 'Geen gestructureerde resumeData in deze koppeling.'}
                    </dd>
                  </div>
                </dl>
              );
            })()}
            {pdfError ? <p className="mt-3 text-xs font-semibold text-red-600">{pdfError}</p> : null}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={pdfLoading || !(value.portalCvSnapshot as Record<string, unknown>)?.templateId}
                onClick={() => void openLinkedDocumentPdf()}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-indigo-200 bg-indigo-600 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none sm:px-6"
              >
                {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <FileText className="h-4 w-4" aria-hidden />}
                Open als PDF
              </button>
              <button
                type="button"
                onClick={() => {
                  setDocumentDetailOpen(false);
                  setPdfError(null);
                }}
                className="inline-flex flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-black uppercase tracking-widest text-slate-700 transition hover:bg-slate-50 sm:flex-none sm:px-6"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!value.publishToPortal && !value.emailToClient ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
          Kies minstens «klantportaal» of «e-mail naar klant» om het voorstel te delen met de klant.
        </p>
      ) : null}
    </div>
  );
};

export default VoorgesteldClientProposalPanel;
